import React, { createContext, useState, useContext, useEffect } from 'react';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    signOut,
    onAuthStateChanged
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { apiRequest } from '../config/api';

const UserContext = createContext();

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // ✅ NEW: Real-time user refresh function
    const refreshUserData = async () => {
        try {
            console.log('🔄 Manually refreshing user data...');
            const currentUser = auth.currentUser;

            if (currentUser) {
                // Get fresh token
                const token = await currentUser.getIdToken(true);
                localStorage.setItem('userToken', token);

                // Fetch latest user data from backend
                const response = await apiRequest('/auth/me');

                if (response.success) {
                    const updatedUser = {
                        ...response.user,
                        photoURL: currentUser.photoURL || response.user.photoURL,
                        emailVerified: currentUser.emailVerified
                    };

                    setUser(updatedUser);
                    localStorage.setItem('userData', JSON.stringify(updatedUser));
                    console.log('✅ User data refreshed successfully:', updatedUser);

                    return { success: true, user: updatedUser };
                }
            }
            return { success: false, message: 'No user found' };
        } catch (error) {
            console.error('❌ Error refreshing user data:', error);
            return { success: false, message: error.message };
        }
    };

    // ✅ NEW: Auto-refresh user data periodically
    useEffect(() => {
        if (user) {
            const interval = setInterval(async () => {
                console.log('🔄 Auto-refreshing user data...');
                await refreshUserData();
            }, 60000); // 1 minute interval

            return () => clearInterval(interval);
        }
    }, [user]);

    // Check Firebase authentication status
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            console.log('🔥 Firebase Auth State Changed:', firebaseUser ? 'User Signed In' : 'User Signed Out');

            if (firebaseUser) {
                const shouldSync = !user;
                if (shouldSync) {
                    await syncUserWithBackend(firebaseUser);
                }
            } else {
                localStorage.removeItem('userToken');
                localStorage.removeItem('userData');
                setUser(null);
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, [user]);

    // Sync Firebase user with our backend
    const syncUserWithBackend = async (firebaseUser) => {
        try {
            setLoading(true);
            console.log('🔄 Syncing user with backend...');

            const token = await firebaseUser.getIdToken(true);
            localStorage.setItem('userToken', token);

            const response = await apiRequest('/auth/me');
            console.log('✅ Backend verification successful:', response);

            if (response.success) {
                const userData = {
                    ...response.user,
                    photoURL: firebaseUser.photoURL || response.user.photoURL,
                    emailVerified: firebaseUser.emailVerified
                };

                setUser(userData);
                localStorage.setItem('userData', JSON.stringify(userData));
                console.log('👤 User data synced:', userData);
            } else {
                throw new Error(response.error || 'Backend verification failed');
            }
        } catch (error) {
            console.error('❌ Auth verification failed:', error);
            if (error.message.includes('Authentication failed') || error.message.includes('Token expired')) {
                await signOut(auth);
                localStorage.removeItem('userToken');
                localStorage.removeItem('userData');
                setUser(null);
                setError('Session expired. Please sign in again.');
            } else {
                localStorage.removeItem('userToken');
                localStorage.removeItem('userData');
                setUser(null);
            }
        } finally {
            setLoading(false);
        }
    };

    // Refresh token manually
    const refreshToken = async () => {
        try {
            const currentUser = auth.currentUser;
            if (currentUser) {
                const token = await currentUser.getIdToken(true);
                localStorage.setItem('userToken', token);
                return token;
            }
            return null;
        } catch (error) {
            console.error('Token refresh failed:', error);
            return null;
        }
    };

    // Google Sign-In function
    const signInWithGoogle = async () => {
        try {
            setError('');
            setLoading(true);
            console.log('🔐 Starting Google Sign-In...');

            const result = await signInWithPopup(auth, googleProvider);
            const firebaseUser = result.user;
            console.log('✅ Google Sign-In successful:', firebaseUser.email);

            return { success: true, message: 'Google Sign-In successful!' };

        } catch (error) {
            console.error('❌ Google Sign-In error:', error);
            let errorMessage = 'Google Sign-In failed. Please try again.';

            if (error.code === 'auth/popup-closed-by-user') {
                errorMessage = 'Sign-in was cancelled';
            } else if (error.code === 'auth/popup-blocked') {
                errorMessage = 'Popup was blocked by browser. Please allow popups for this site.';
            } else if (error.code === 'auth/unauthorized-domain') {
                errorMessage = 'This domain is not authorized for Google Sign-In.';
            } else if (error.message) {
                errorMessage = error.message;
            }

            setError(errorMessage);
            return { success: false, message: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    // Email/Password Login function
    const login = async (email, password) => {
        try {
            setError('');
            setLoading(true);
            console.log('🔐 Starting Email/Password Login...');

            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const firebaseUser = userCredential.user;
            console.log('✅ Email/Password Login successful:', firebaseUser.email);

            return { success: true, message: 'Login successful!' };

        } catch (error) {
            console.error('❌ Login error:', error);
            let errorMessage = 'Login failed. Please try again.';

            if (error.code === 'auth/invalid-credential') {
                errorMessage = 'Invalid email or password';
            } else if (error.code === 'auth/user-not-found') {
                errorMessage = 'No account found with this email';
            } else if (error.code === 'auth/wrong-password') {
                errorMessage = 'Incorrect password';
            } else if (error.code === 'auth/too-many-requests') {
                errorMessage = 'Too many failed attempts. Please try again later.';
            } else if (error.code === 'auth/operation-not-allowed') {
                errorMessage = 'Email/password authentication is not enabled. Please use Google Sign-In.';
            } else if (error.message) {
                errorMessage = error.message;
            }

            setError(errorMessage);
            return { success: false, message: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    // Email/Password Register function
    const register = async (userData) => {
        try {
            setError('');
            setLoading(true);
            console.log('🔐 Starting Email/Password Registration...');

            const { email, password, name, phone, address } = userData;

            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const firebaseUser = userCredential.user;
            console.log('✅ Firebase registration successful:', firebaseUser.email);

            const token = await firebaseUser.getIdToken();
            localStorage.setItem('userToken', token);

            const registerResponse = await apiRequest('/auth/register', {
                method: 'POST'
            });

            if (registerResponse.success) {
                console.log('✅ Backend registration successful');
                return {
                    success: true,
                    message: 'Registration successful!',
                };
            } else {
                throw new Error(registerResponse.error || 'Backend registration failed');
            }

        } catch (error) {
            console.error('❌ Registration error:', error);
            let errorMessage = 'Registration failed. Please try again.';

            if (error.code === 'auth/email-already-in-use') {
                errorMessage = 'An account already exists with this email';
            } else if (error.code === 'auth/weak-password') {
                errorMessage = 'Password should be at least 6 characters';
            } else if (error.code === 'auth/invalid-email') {
                errorMessage = 'Invalid email address';
            } else if (error.code === 'auth/operation-not-allowed') {
                errorMessage = 'Email/password registration is not enabled. Please use Google Sign-In.';
            } else if (error.message) {
                errorMessage = error.message;
            }

            setError(errorMessage);
            return { success: false, message: errorMessage };
        } finally {
            setLoading(false);
        }
    };

    // Update profile function
    const updateProfile = async (profileData) => {
        try {
            setError('');

            const response = await apiRequest('/auth/profile', {
                method: 'PUT',
                body: profileData
            });

            if (response.success) {
                localStorage.setItem('userData', JSON.stringify(response.user));
                setUser(response.user);
                return { success: true, message: response.message };
            } else {
                throw new Error(response.error || 'Profile update failed');
            }
        } catch (error) {
            const errorMessage = error.message || 'Profile update failed. Please try again.';
            setError(errorMessage);
            return { success: false, message: errorMessage };
        }
    };

    // Request admin role
    const requestAdminRole = async () => {
        try {
            setError('');
            console.log('🔄 Requesting admin role...');

            const response = await apiRequest('/auth/register-admin-request', {
                method: 'POST'
            });

            if (response.success) {
                console.log('✅ Admin request submitted successfully');

                // ✅ IMPORTANT: Refresh user data after request
                await refreshUserData();

                return {
                    success: true,
                    message: response.message || 'Admin request submitted successfully!'
                };
            } else {
                throw new Error(response.error || 'Admin request failed');
            }
        } catch (error) {
            console.error('❌ Admin request error:', error);
            const errorMessage = error.message || 'Admin request failed. Please try again.';
            setError(errorMessage);
            return {
                success: false,
                message: errorMessage
            };
        }
    };

    // Logout function
    const logout = async () => {
        try {
            console.log('🚪 Logging out...');
            await signOut(auth);
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('userToken');
            localStorage.removeItem('userData');
            setUser(null);
            setError('');
        }
    };

    const value = {
        user,
        login,
        register,
        signInWithGoogle,
        updateProfile,
        requestAdminRole,
        logout,
        refreshToken,
        refreshUserData, // ✅ NEW: Export refresh function
        loading,
        error,
        setError,
        apiRequest
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};